/* eslint-disable curly */
/* eslint-disable prettier/prettier */
import { computedEager, createSharedComposable } from "@vueuse/core";
import aa from "search-insights";
import { useLanguages } from "@/core/composables";
import { globals } from "@/core/globals";
import { Logger } from "@/core/utilities";
import { getIndexName } from "../thorlabs/search";
import type { IAAClickEventData, IAAEventData, IAAQueryData, IAlgoliaSearchData } from "../layout/components/search-bar/searchTypes";
import type { CustomerOrderTypeExtension, LineItemTypeExtension, OrderLineItemTypeExtension } from "@/core/api/graphql/types";
import type {Ref} from "vue";

/**
 * A name for URL query parameter which will contain Algolia query ID, and with that we know the product is viewed from search or related to it
 */
export const algoliaQueryIDParamName : string = "aID";

/**
 * A name for URL query parameter which will contain Algolia index name, and with that we know the product is viewed from search or related to it
 */
//export const algoliaIndexNameParamName : string = "aIdx";

/**
 * Name for URL query param which will hold base64 encoded json of queryID and index name
 */
export const algoliaQueryDataParamName : string = "aa";

/**
 * Name for URL query param which will hold Algolia index code.
 * Index code is used to avoid passing index name directly in URL query params.
 * Check out `algoliaIndexNameCodes` dictionary for mapping index codes to index names.
 */
export const algoliaIndexCodeParamName : string = "aC";

/**
 * Name for URL query parameter which will hold position of where in the search results was item clicked
 */
export const algoliaItemPositionParamName : string = "aN";

/**
 * Name for URL query parameter which will signal if manual click events needs to be sent.
 * This is for places where automatic click events don't work like main search page and new products page.
 */
export const algoliaSendEventParamName : string = "aE";

/**
 * Name used for storing dynamic property in cart line items to know from which search they come
 */
export const algoliaDynamicPropertyName : string = "algoliaQueryIDAndIndex";

/**
 * Dictionary used to map Algolia index name in query param.
 * We want to avoid passing index name directly in URL query params, so instead we will pass codes of the index.
 */
export const algoliaIndexNameCodes : Record<string, string> = {
    "1" : "products",
    "2" : "familypages"
};

export const useThorlabsAlgoliaAnalytics = createSharedComposable(_useThorlabsAlgoliaAnalytics);

function _useThorlabsAlgoliaAnalytics()
{
    const hasOneTrustMarketingConsent = computedEager(() =>
    {
        const cookieValue = getCookieValue("OptanonConsent") as string | undefined;
        return cookieValue?.includes("C0004") ?? false; //C0004 is for marketing cookies
    });

    const currentAlgoliaQueryData : Readonly<Ref<IAAQueryData | undefined>> = computedEager(() =>
    {
        const router = globals.router;

        const queryID = router?.currentRoute?.value?.query[algoliaQueryIDParamName] as string | undefined;
        const indexCode = router?.currentRoute?.value?.query[algoliaIndexCodeParamName] as string | undefined;

        const indexName = convertIndexCodeToIndexName(indexCode);

        if (!queryID || queryID.length === 0 || !indexName || indexName.length === 0)
        {
            Logger.debug(`Algolia analytics - no query data in URL (either queryID or index code is missing - '${algoliaQueryIDParamName}' and '${algoliaIndexCodeParamName}' params) - indexCode: ${indexCode}, queryID: ${queryID}`);
            return undefined;
        }

        const queryData : IAAQueryData = {
            queryID: queryID,
            index: indexName
        };

        return queryData;
    });

    //If currently opened page came from algolia search, get query ID
    const currentAlgoliaQueryID = computedEager(() =>
    {
        return currentAlgoliaQueryData.value?.queryID;
    });

    const currentAlgoliaQueryIndex = computedEager(() =>
    {
        return currentAlgoliaQueryData.value?.index;
    });

    //Used to determine if opened product or family page is opened because it came from Algolia search
    const isFromAlgoliaSearch = computedEager(() =>
    {
        return currentAlgoliaQueryID.value !== undefined && currentAlgoliaQueryID.value.length > 0;
    });

    const algoliaAddCurrentSearchParamsToURL = (baseURL? : string, shouldSendClickEvent? : boolean, itemPosition? : string) =>
    {
        if (false === isFromAlgoliaSearch.value)
            return baseURL;

        return addAlgoliaParametersToUrl(baseURL, currentAlgoliaQueryID.value, currentAlgoliaQueryIndex.value, shouldSendClickEvent, itemPosition);
    }

    const algoliaSendAddedToCartObjectIDsAfterSearch = async ( addedLineItems: LineItemTypeExtension[] | undefined ) =>
    {
        if (!addedLineItems || addedLineItems.length === 0)
        {
            Logger.debug("Algolia analytics - not sending anything, since no line items added to cart");
            return;
        }

        if (false === isFromAlgoliaSearch.value)
        {
            Logger.debug("Algolia analytics - not sending anything, since not detecting alID in URL");
            return;
        }

        //Make sure to calculate cart amount in USD no matter what is the actuall currency of cart
        const cartAmount = roundToTwoDecimals(addedLineItems?.reduce((sum, item) => sum + getUSDPriceAmountFromLineItem(item) * item.quantity, 0) ?? 0);

        const eventData : IAAEventData =  {
            eventName: 'added_to_cart',
            queryID: currentAlgoliaQueryID.value ?? "",
            index: currentAlgoliaQueryIndex.value ?? "",
            objectIDs: addedLineItems?.map(item => item.product?.code ?? ""),
            objectData: addedLineItems?.map(item => ({ price: getUSDPriceAmountFromLineItem(item), quantity: item.quantity })),
            value: cartAmount,
            currency: "USD",
        }

        if (hasOneTrustMarketingConsent.value && globals.isAuthenticated && globals.userId)
            eventData['authenticatedUserToken'] = globals.userId;

        await aa('addedToCartObjectIDsAfterSearch', eventData);

    }

    const algoliaSendPurchasedObjectIDsAfterSearch  = async ( order : CustomerOrderTypeExtension | undefined ) =>
    {
        if (!order)
        {
            Logger.debug("Algolia analytics - not sending anything, no order is provided");
            return;
        }

        //Find all line items with Algolia search data
        const lineItemsFromSearch = order.items.filter( item => item.dynamicProperties?.some(prop => prop?.name === algoliaDynamicPropertyName && (prop.value as string | undefined)?.length !== 0) )

        if (!lineItemsFromSearch || lineItemsFromSearch.length === 0)
        {
            Logger.debug("Algolia analytics - not sending anything, since no line items have algolia search data (queryID and indexName)");
            return;
        }

        const groupedLineItems = lineItemsFromSearch.reduce<Record<string, OrderLineItemTypeExtension[]>>((dict, item) => {
            const queryIdAndIndex = item.dynamicProperties?.find(prop => prop.name === algoliaDynamicPropertyName)?.value as string | undefined;

            if (!queryIdAndIndex || queryIdAndIndex.length === 0)
                return dict;

            const [queryID,] = queryIdAndIndex.split("~") ?? [];

            if (!dict[queryID])
                dict[queryID] = [];

            dict[queryID].push(item);

            return dict;
        }, {});

        //Iterate line items grouped by query ID and send analytics event for each group
        for (const [queryID, lineItemGroup] of Object.entries(groupedLineItems))
        {
            if (!queryID || queryID.length === 0 || !lineItemGroup || lineItemGroup.length === 0)
                continue;

            const queryIdAndIndex = lineItemGroup.at(0)?.dynamicProperties?.find(prop => prop.name === algoliaDynamicPropertyName)?.value as string | undefined;
            const [, indexName] = queryIdAndIndex?.split("~") ?? [];

            const orderAmount = roundToTwoDecimals(lineItemGroup?.reduce((sum, item) => sum + getUSDPriceAmountFromLineItem(item) * item.quantity, 0) ?? 0);

            const eventData : IAAEventData = {
                eventName: 'purchase',
                queryID: queryID ?? "",
                index: indexName ?? "",
                objectIDs: lineItemGroup?.map(item => item.sku ?? ""),
                objectData: lineItemGroup?.map(item => ({ price: getUSDPriceAmountFromLineItem(item), quantity: item.quantity })),
                value: orderAmount,
                currency: "USD",
            }

            if (hasOneTrustMarketingConsent.value && globals.isAuthenticated && globals.userId)
                eventData['authenticatedUserToken'] = globals.userId;

            await aa('purchasedObjectIDsAfterSearch', eventData);
        }

    }

    const algoliaSendClickedObjectIDsAfterSearch = async ( objectID? : string, position?: number ) =>
    {
        Logger.debug(objectID);
        Logger.debug(position);
        if (!objectID || objectID.length === 0 || position === undefined || isNaN(position))
        {
            Logger.debug("Algolia analytics - not sending click event since objectID is empty or item position is empty");
            return;
        }

        if (false === isFromAlgoliaSearch.value)
        {
            Logger.debug("Algolia analytics - not sending anything, since not detecting queryID in URL params");
            return;
        }

        const eventData : IAAClickEventData =  {
            eventName: 'Item Clicked',
            queryID: currentAlgoliaQueryID.value ?? "",
            index: currentAlgoliaQueryIndex.value ?? "",
            objectIDs: [objectID],
            currency: globals.currencyCode,
            positions: position !== undefined ? [position] : []
        }

        if (hasOneTrustMarketingConsent.value && globals.isAuthenticated && globals.userId)
            eventData['authenticatedUserToken'] = globals.userId;

        await aa('clickedObjectIDsAfterSearch', eventData);

    }

    return {
        currentAlgoliaQueryData,
        currentAlgoliaQueryID,
        currentAlgoliaQueryIndex,
        isFromAlgoliaSearch,
        algoliaAddCurrentSearchParamsToURL,
        algoliaSendAddedToCartObjectIDsAfterSearch,
        algoliaSendPurchasedObjectIDsAfterSearch,
        algoliaSendClickedObjectIDsAfterSearch
    }
}

export function addQueryParameterToUrl(url? : string, name?: string, value? : string) : string
{
    if (!url)
        return "#";

    if (!name || !value)
        return url || "#";

    if (url.endsWith("?") || url.endsWith("&"))
        return `${url}${name}=${value}`;

    if (url.includes("?"))
        return `${url}&${name}=${value}`;

    return `${url}?${name}=${value}`;
}

const getCookieValue = (cookieName: string): string | undefined => {
    const cookies = document.cookie.split("; ");
    // eslint-disable-next-line sonarjs/prefer-immediate-return
    const cookie = cookies.find((row) => row.startsWith(`${cookieName}=`));
    return cookie;
};

export function convertAlgoliaSearchDataToBase64(queryID?: string, indexName?: string): string | undefined
{
    if (!queryID || !indexName)
        return undefined;

    const queryData : IAAQueryData = { queryID, index: indexName };
    const queryDataJsonString = JSON.stringify(queryData);
    return btoa(queryDataJsonString);
}

/**
 *
 * This will create URL with Algolia query ID and index name code (used for sending Algolia analytics events).
 *
 */
export function addAlgoliaParametersToUrlFromSearchItem( baseURL? : string, searchItem? : IAlgoliaSearchData, shouldSendClickEvent? : boolean, itemPosition? : string ): string | undefined
{
    if (!baseURL || !searchItem)
    {
        Logger.debug("Algolia analytics - no base URL or search item provided");
        return baseURL;
    }


    if (!searchItem.__autocomplete_queryID && !searchItem.__queryID && !searchItem.queryID)
    {
        Logger.debug("Algolia analytics - no query ID in search item");
        return baseURL;
    }

    let newURL = baseURL;
    newURL = addQueryParameterToUrl(newURL, algoliaQueryIDParamName, searchItem.__autocomplete_queryID || searchItem.__queryID || searchItem.queryID);
    newURL = addQueryParameterToUrl(newURL, algoliaIndexCodeParamName, convertIndexNameToIndexCode(searchItem.__autocomplete_indexName || searchItem.__indexName || searchItem.index));

    if (shouldSendClickEvent === true && itemPosition !== undefined)
    {
        newURL = addQueryParameterToUrl(newURL, algoliaSendEventParamName, "1"); //1 here is used as bool value and not for counting anything
        newURL = addQueryParameterToUrl(newURL, algoliaItemPositionParamName, itemPosition);
    }

    return newURL;
}

export function addAlgoliaParametersToUrl( baseURL? : string, queryID? : string, indexName? : string, shouldSendClickEvent? : boolean, itemPosition? : string ): string | undefined
{
    return addAlgoliaParametersToUrlFromSearchItem(baseURL, { __queryID: queryID, __indexName: indexName } as IAlgoliaSearchData, shouldSendClickEvent, itemPosition);
}


export function convertIndexCodeToIndexName(indexCode? : string) : string | undefined
{
    if (!indexCode || !algoliaIndexNameCodes[indexCode])
    {
        Logger.debug(`Algolia analytics - no index code or index name for code '${indexCode}'`);
        return undefined;
    }

    const indexName = algoliaIndexNameCodes[indexCode];

    const { currentLocale } = useLanguages();
    // eslint-disable-next-line sonarjs/prefer-immediate-return
    const fullIndexName = getIndexName(indexName, currentLocale.value);

    return fullIndexName;
}

/**
 * Converts index name to code using dictionary.
 *
 * @param fullIndexName - full index name, e.g. "inte_products_en"
 * @returns
 */
export function convertIndexNameToIndexCode(fullIndexName? : string) : string | undefined
{
    if (!fullIndexName)
    {
        Logger.debug("Algolia analytics - no index name provided");
        return undefined;
    }

    let indexCode = undefined;
    for (const [keyCode, valueName] of Object.entries(algoliaIndexNameCodes))
    {
        if (fullIndexName.includes(valueName))
        {
            indexCode = keyCode;
            break;
        }
    }

    if (!indexCode)
    {
        Logger.debug(`Algolia analytics - no index code found for index name '${fullIndexName}'`);
        return undefined;
    }

    return indexCode;
}

export function roundToTwoDecimals(amount: number): number
{
    const cents = amount * 100;
    return Math.round(cents + Number.EPSILON * Math.abs(cents)) / 100;
}

export function getUSDPriceAmountFromLineItem(lineItem: LineItemTypeExtension | OrderLineItemTypeExtension | undefined): number
{
    if (lineItem?.placedPrice?.currency.code === "USD")
    {
        return roundToTwoDecimals(lineItem.placedPrice?.amount ?? NaN);
    }
    else
    {
        return roundToTwoDecimals(lineItem?.product?.allCurrenciesPricesTL?.find(price => price.list?.currency.code === "USD")?.list?.amount ?? NaN);
    }
}


